import { useEffect, useState } from 'react';
import { IonButton, IonChip, IonIcon, IonLabel } from '@ionic/react';
import {
  carSportOutline,
  handRightOutline,
  bookmarksOutline,
  downloadOutline,
  peopleOutline,
  settingsOutline,
} from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { AppUpdate } from '../components/AppUpdate';
import { connections as connectionsApi } from '../lib/connections';
import { androidBrowser, apkDownload, formatSize } from '../lib/update';
import type { Connection } from '../lib/types';

interface Props {
  onDrive: () => void;
  onRide: () => void;
  onPlaces: () => void;
  onContacts: () => void;
  onStartRideWithContact: (contact: Connection) => void;
  /** Serveradresse (nur in der App) und der API-Key fuer die Karte. */
  onServerSettings: () => void;
  /** Fahrten, die dieses Geraet als Fahrer angelegt und noch nicht beendet hat. */
  activeRideCount: number;
  onActiveRides: () => void;
}

const INTRO_SEEN_KEY = 'r2f.introSeen';

/** Nur beim allerersten Öffnen wahr - danach kennt man die App schon. */
function isFirstVisit(): boolean {
  try {
    if (localStorage.getItem(INTRO_SEEN_KEY)) return false;
    localStorage.setItem(INTRO_SEEN_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

export function Home({
  onDrive,
  onRide,
  onPlaces,
  onContacts,
  onStartRideWithContact,
  onServerSettings,
  activeRideCount,
  onActiveRides,
}: Props) {
  const [contacts, setContacts] = useState<Connection[]>([]);
  const [showIntro] = useState(isFirstVisit);
  // Nur im Android-Browser: die Datei, mit der aus der Webseite eine App wird.
  const [apk, setApk] = useState<{ name: string; size: number; url: string } | null>(null);

  useEffect(() => {
    if (!androidBrowser()) return;
    // Bietet der Server keine Updates an (oder gibt es noch keinen Release),
    // bleibt die Karte einfach weg - ein toter Knopf waere schlimmer.
    apkDownload()
      .then(setApk)
      .catch(() => setApk(null));
  }, []);

  // Fehlschlag ist hier kein Fehler wert, der die Startseite unterbricht -
  // ohne Kontakte zeigt sie einfach die Kacheln wie eh und je.
  useEffect(() => {
    connectionsApi
      .list()
      .then(({ connections: list }) => setContacts(Array.isArray(list) ? list : []))
      .catch(() => setContacts([]));
  }, []);

  return (
    <Page>
      <div className="r2f-hero">
        <h1>Abholen,<br />ohne Nachfragen.</h1>
        {showIntro && (
          <p>
            Der Fahrer startet die Fahrt, du bekommst Bescheid - beim Losfahren, kurz vor der
            Ankunft und wenn er da ist.
          </p>
        )}
      </div>

      {/* Meldet sich nur, wenn es in der App wirklich eine neuere Version
          gibt - im Browser und ohne Update nie. */}
      <AppUpdate variant="banner" />

      {/* Eine laufende Fahrt ist dringlicher als eine neue - sie bekommt die
          hervorgehobene Kachel, "Ich fahre" tritt dann einen Schritt zurück. */}
      {activeRideCount > 0 && (
        <button type="button" className="r2f-choice r2f-choice--primary" onClick={onActiveRides}>
          <span className="r2f-choice__icon">
            <IonIcon icon={carSportOutline} />
          </span>
          <span>
            <h2>{activeRideCount === 1 ? '1 laufende Fahrt' : `${activeRideCount} laufende Fahrten`}</h2>
            <p>Weiter verfolgen oder beenden.</p>
          </span>
        </button>
      )}

      {apk && (
        <Card>
          <h2>R2F als Android-App</h2>
          <p>
            Im Browser schläft die Seite ein, sobald das Display ausgeht - Standort und
            Benachrichtigungen bleiben dann aus. Die App hält beides wach.
          </p>
          <IonButton expand="block" href={apk.url} download={apk.name} style={{ marginTop: 12 }}>
            <IonIcon slot="start" icon={downloadOutline} />
            App laden{apk.size ? ` (${formatSize(apk.size)})` : ''}
          </IonButton>
          <p className="r2f-muted" style={{ marginBottom: 0 }}>
            Android fragt beim Öffnen der Datei nach der Erlaubnis, Apps aus dieser Quelle zu
            installieren.
          </p>
        </Card>
      )}

      <button
        type="button"
        className={activeRideCount > 0 ? 'r2f-choice' : 'r2f-choice r2f-choice--primary'}
        onClick={onDrive}
      >
        <span className="r2f-choice__icon">
          <IonIcon icon={carSportOutline} />
        </span>
        <span>
          <h2>Ich fahre</h2>
          <p>Startpunkt setzen und den Code weitergeben.</p>
        </span>
      </button>

      <button type="button" className="r2f-choice" onClick={onRide}>
        <span className="r2f-choice__icon">
          <IonIcon icon={handRightOutline} />
        </span>
        <span>
          <h2>Ich werde abgeholt</h2>
          <p>Code eingeben und benachrichtigen lassen.</p>
        </span>
      </button>

      {contacts.length > 0 && (
        <>
          <div className="r2f-sectionhead">
            <h2>Schnellstart</h2>
            <IonButton fill="clear" size="small" onClick={onContacts}>
              Bearbeiten
            </IonButton>
          </div>
          <div className="r2f-chiprow">
            {contacts.map((contact) => (
              <IonChip key={contact.id} onClick={() => onStartRideWithContact(contact)}>
                <IonIcon icon={carSportOutline} />
                <IonLabel>{contact.label}</IonLabel>
              </IonChip>
            ))}
          </div>
        </>
      )}

      <div className="r2f-sectionhead">
        <h2>Verwalten</h2>
      </div>

      <button type="button" className="r2f-choice" onClick={onContacts}>
        <span className="r2f-choice__icon">
          <IonIcon icon={peopleOutline} />
        </span>
        <span>
          <h2>Meine Kontakte</h2>
          <p>Einmal verbinden, künftig ohne Code starten.</p>
        </span>
      </button>

      <button type="button" className="r2f-choice" onClick={onPlaces}>
        <span className="r2f-choice__icon">
          <IonIcon icon={bookmarksOutline} />
        </span>
        <span>
          <h2>Meine Orte</h2>
          <p>Gespeicherte Adressen verwalten.</p>
        </span>
      </button>

      <button type="button" className="r2f-choice" onClick={onServerSettings}>
        <span className="r2f-choice__icon">
          <IonIcon icon={settingsOutline} />
        </span>
        <span>
          <h2>Einstellungen</h2>
          <p>Serveradresse und API-Key für die Karte.</p>
        </span>
      </button>

      {showIntro && (
        <Card>
          <h2>So funktioniert es</h2>
          <p>
            Sobald sich der Fahrer mehr als 300 Meter von seinem Startpunkt entfernt, geht die
            erste Nachricht raus - mit geschätzter Entfernung und Fahrzeit. Die zweite kommt,
            wenn er noch etwa zwei Minuten braucht.
          </p>
        </Card>
      )}
    </Page>
  );
}
